/**
 * A utility function that composes multiple functions into a single function.
 * Functions are applied from left to right (first to last).
 *
 * The first function can accept multiple arguments, while subsequent functions
 * receive the single result from the previous function.
 *
 * @example
 * // Single argument
 * const processEntity = pipeFunctions(
 *   removeDomainEntityId
 * );
 * const result = processEntity(entityData);
 *
 * @example
 * // Multiple arguments
 * const processData = pipeFunctions(
 *   (data, schema) => regenerateIds(data, schema),
 *   (result) => validateResult(result)
 * );
 * const result = processData(entityData, entitySchema);
 */
export function pipeFunctions<R>(...fns: Array<(x: R) => R>): (arg: R) => R;
export function pipeFunctions<T extends any[], R>(
  firstFn: (...args: T) => R,
  ...fns: Array<(x: R) => R>
): (...args: T) => R;
export function pipeFunctions<T extends any[], R>(
  ...fns: Array<(...args: any) => any>
): (...args: T) => R {
  return (...args: T) => {
    if (fns.length === 0) {
      throw new Error('pipeFunctions requires at least one function');
    }

    // Apply the first function with all arguments
    let result = fns[0](...args);

    // Apply remaining functions with single argument (the result)
    for (let i = 1; i < fns.length; i += 1) {
      result = fns[i](result);
    }

    return result;
  };
}
